import { useEffect, useState } from "react";
import { useLogo } from "./logo.ts";
import { PRINT_PDF_EVENT, closePrintPdf, printPdf, sharePrintPdf } from "./print.ts";
import { store, useStore } from "./store.ts";
import type { SignedIn } from "./store.ts";
import { update } from "./update.ts";
import { useWide } from "./wide.ts";
import { left, tabOf } from "./tabs.ts";
import { keepScroll, keptFor, restoreScroll } from "./kept.ts";
import { Account } from "./views/Account.tsx";
import { Allergens } from "./views/Allergens.tsx";
import { Categories } from "./views/Categories.tsx";
import { Tags } from "./views/Tags.tsx";
import { Texts } from "./views/Texts.tsx";
import { Review } from "./views/Review.tsx";
import { OwnPages } from "./views/OwnPages.tsx";
import { Choose } from "./views/Choose.tsx";
import { ConfirmEmail } from "./views/Email.tsx";
import { Dishes } from "./views/Dishes.tsx";
import { Editor } from "./views/Editor.tsx";
import { Import } from "./views/Import.tsx";
import { Reading } from "./views/Reading.tsx";
import { Join, Reset } from "./views/Links.tsx";
import { Login } from "./views/Login.tsx";
import { Menus } from "./views/Menus.tsx";
import { Arrive, MoveOver, MovedBanner, handOver } from "./views/Move.tsx";
import { siteInfo } from "./account.ts";
import { Signup } from "./views/Signup.tsx";
import { SoldOut } from "./views/SoldOut.tsx";
import { Web, outdated } from "./views/Web.tsx";
import { PlanFlow } from "./views/Plan.tsx";

const TABS = [
  { route: "speisekarten", label: "Karten" },
  { route: "kataloge", label: "Kataloge" },
  { route: "netz", label: "Im Netz" },
  { route: "konto", label: "Konto" },
] as const;
/** Views that use the whole width on a wide screen. */
const WIDE_VIEWS = new Set(["speisekarte", "kataloge", "speise", "getraenke", "getraenk", "texte-pruefen"]);

function useRoute(): { route: string; arg: string; params: URLSearchParams } {
  const read = () => {
    const [path = "", query = ""] = location.hash.replace(/^#\/?/, "").split("?");
    const [route = "", ...rest] = path.split("/");
    return { route: route || "speisekarten", arg: rest.join("/"), params: new URLSearchParams(query) };
  };
  const [r, setR] = useState(read);
  useEffect(() => {
    const on = (e: HashChangeEvent) => {
      // Where the view left was scrolled to, and back there when one returns to a view.
      keepScroll(new URL(e.oldURL).hash);
      setR(read());
      restoreScroll(location.hash);
    };
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return r;
}

function Badge() {
  const s = useStore();
  let [text, cls] = ["aktuell", ""];
  if (!s.online) [text, cls] = ["offline", "offline"];
  else if (s.lastError) [text, cls] = ["Fehler", "error"];
  else if (s.loading) [text, cls] = ["lädt …", ""];
  return (
    <span className={`badge ${cls}`} title={s.lastError ?? (s.online ? "Mit dem Server verbunden" : "Ohne Netz: ansehen, drucken und herunterladen geht, ändern nicht")} data-testid="badge">
      {text}
    </span>
  );
}

function UpdateBanner() {
  const [ready, setReady] = useState(update.ready);
  useEffect(() => {
    const on = () => setReady(true);
    window.addEventListener("speisekarte:update", on);
    // Offered between the first drawing and this listener (the first API answers come that early).
    if (update.ready) setReady(true);
    return () => window.removeEventListener("speisekarte:update", on);
  }, []);
  // „Neu laden“ answers at once (Martin, 02.11.2026: a second or two without a sign looked broken):
  // the button busy, the app covered with what happens, until the new version is there.
  const [state, setState] = useState<"idle" | "busy" | "failed">("idle");
  if (!ready) return null;
  async function load() {
    setState("busy");
    if (!(await update.apply())) setState("failed");
  }
  return (
    <>
      <div className="banner" aria-live="polite" data-testid="update-banner">
        {state === "failed" ? "Die neue Version ließ sich gerade nicht laden; ist das Gerät online?" : "Es gibt eine neue Version."}{" "}
        <button onClick={() => void load()} disabled={state === "busy"} aria-busy={state === "busy"} data-testid="update-load">
          {state === "busy" ? (
            <>
              <span className="spinner" aria-hidden="true" /> Wird geladen …
            </>
          ) : state === "failed" ? (
            "Noch einmal"
          ) : (
            "Neu laden"
          )}
        </button>
      </div>
      {state === "busy" && (
        <div className="reloading" role="status" data-testid="update-reloading">
          <p>
            <span className="spinner" aria-hidden="true" /> Neue Version wird geladen …
          </p>
        </div>
      )}
    </>
  );
}

/** Printing in the app on an iPhone's home screen: the PDF, then the share sheet (print.ts). */
function PrintSheet() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const on = () => setTick((n) => n + 1);
    // Another page closes the sheet: it belongs to what was being printed (A2, 25.10.2026).
    const away = () => (printPdf.busy || printPdf.file || printPdf.error) && closePrintPdf();
    window.addEventListener(PRINT_PDF_EVENT, on);
    window.addEventListener("hashchange", away);
    return () => {
      window.removeEventListener(PRINT_PDF_EVENT, on);
      window.removeEventListener("hashchange", away);
    };
  }, []);
  if (!printPdf.busy && !printPdf.file && !printPdf.error) return null;
  return (
    <div className="print-sheet" role="dialog" aria-label="Drucken" data-testid="print-sheet">
      {printPdf.busy && <p>PDF wird erstellt …</p>}
      {printPdf.file && (
        <>
          <p>Das PDF ist fertig. Im nächsten Schritt „Drucken“ wählen.</p>
          <button className="primary" onClick={() => void sharePrintPdf().then(closePrintPdf)} data-testid="print-share">
            Drucken …
          </button>
        </>
      )}
      {printPdf.error && <p className="error">{printPdf.error}</p>}
      <button className="link" onClick={closePrintPdf}>
        Abbrechen
      </button>
    </div>
  );
}

/** The session ran out: what the device holds stays readable; changing needs a new sign-in. */
function SignInAgain() {
  const s = useStore();
  if (!s.signedOut) return null;
  return (
    <div className="banner">
      Die Anmeldung ist abgelaufen. Ansehen und Drucken gehen weiter. <a href="#/anmelden">Neu anmelden</a>
    </div>
  );
}

export function App() {
  const s = useStore();
  const { route, arg, params } = useRoute();
  const [signedIn, setSignedIn] = useState<SignedIn | null>(null);
  // Signed in under an old address (S30): taken along to the new one; without a network to it, here.
  const onLogin = async (s: SignedIn) => {
    if (await siteInfo().then((site) => site.moved, () => false)) {
      try {
        return await handOver();
      } catch {
        // stays here, the banner says where to go
      }
    }
    setSignedIn(s);
  };
  const wide = useWide();
  useLogo();
  const workspace = s.session?.workspaceId ?? "";
  if (left.workspace !== workspace) Object.assign(left, { workspace, at: new Map() });
  keptFor(workspace);
  if (!s.ready) return <p className="loading">SpeiseKarte startet …</p>;
  // Links from an invitation or a reset work whether or not someone is signed in on this device.
  if (!signedIn && route === "einladung") return <Join token={arg} onSignedIn={setSignedIn} />;
  if (!signedIn && route === "passwort") return <Reset token={arg} onSignedIn={setSignedIn} />;
  // The move (S30): under the new address the key from the old one; under the old one, „take me there“.
  if (!signedIn && route === "umzug" && arg) return <Arrive moveKey={arg} onSignedIn={setSignedIn} />;
  if (!signedIn && route === "umziehen") return <MoveOver onSignedIn={(s) => void onLogin(s)} />;
  if (signedIn) return <Choose signedIn={signedIn} key={signedIn.user.id} onDone={() => setSignedIn(null)} />;
  if (!s.session || route === "anmelden") {
    if (route === "registrieren") return <Signup onSignedIn={setSignedIn} />;
    // The link to confirm an address works only signed in as its account (S28).
    if (route === "email") {
      return (
        <>
          <div className="banner" data-testid="email-signin">Zum Bestätigen deiner E-Mail-Adresse bitte mit deinem Konto anmelden.</div>
          <Login onSignedIn={(s) => void onLogin(s)} />
        </>
      );
    }
    return <Login onSignedIn={(s) => void onLogin(s)} />;
  }
  // Signed out with nothing on this device (as after a new data format, S19): the sign-in, else „Lädt …“ for ever.
  if (!s.data && s.signedOut) {
    return (
      <>
        <div className="banner">Die Anmeldung ist abgelaufen. Bitte neu anmelden.</div>
        <Login onSignedIn={(s) => void onLogin(s)} />
      </>
    );
  }
  if (!s.data) return <p className="loading">{s.lastError ?? (s.online ? "Lädt …" : "Ohne Netz und noch nichts auf diesem Gerät. Bitte mit Netz öffnen.")}</p>;
  const tab = tabOf(route);
  /** Back to where the tab was left, unless that menu or dish is gone by now. */
  const hrefOf = (to: string) => {
    const at = tab === to ? undefined : left.at.get(to);
    const [, view, id] = /^#\/(speisekarte|speise|getraenk)\/([^?/]+)/.exec(at ?? "") ?? [];
    const gone = id && id !== "neu" && !(view === "speisekarte" ? s.data!.menus : s.data!.dishes).some((x) => x.id === id);
    return at && !gone ? at : `#/${to}`;
  };
  const view =
    route === "email" ? (
      <ConfirmEmail token={arg} />
    ) : route === "speisekarte" ? (
      <Editor key={`${arg}?${params.toString()}`} id={arg} params={params} />
    ) : route === "kataloge" || route === "speise" ? (
      <Dishes selected={route === "speise" ? arg : null} />
    ) : route === "getraenke" || route === "getraenk" ? (
      <Dishes key="drinks" kind="drink" selected={route === "getraenk" ? arg : null} />
    ) : route === "allergene" ? (
      <Allergens />
    ) : route === "kategorien" ? (
      <Categories kind={params.get("art") === "getraenke" ? "drink" : "food"} />
    ) : route === "tags" ? (
      <Tags kind={params.get("art") === "getraenke" ? "drink" : "food"} />
    ) : route === "textbausteine" ? (
      <Texts focus={arg} />
    ) : route === "texte-pruefen" || route === "uebersetzungen" ? (
      // „Übersetzungen“ became „Texte prüfen und übersetzen“ (S61); an old link still finds it.
      <Review key={`${arg}?${params.toString()}`} menuId={route === "texte-pruefen" ? arg : ""} params={params} />
    ) : route === "eigene-seiten" ? (
      <OwnPages />
    ) : route === "import" ? (
      <Import />
    ) : route === "einlesen" ? (
      <Reading />
    ) : route === "netz" ? (
      <Web />
    ) : route === "wochenplan" ? (
      <PlanFlow />
    ) : route === "ausverkauft" ? (
      <SoldOut />
    ) : route === "konto" ? (
      <Account />
    ) : (
      <Menus />
    );
  return (
    <div className="screen">
      <header className="bar">
        <strong>{store.workspaceName}</strong>
        <Badge />
      </header>
      <UpdateBanner />
      <MovedBanner signedIn />
      <SignInAgain />
      <PrintSheet />
      <main className={wide && WIDE_VIEWS.has(route) ? "wide" : ""}>{view}</main>
      <nav className="tabs">
        {TABS.map((t) => (
          <a key={t.route} href={hrefOf(t.route)} onClick={() => void left.at.set(tab, location.hash)} className={tab === t.route ? "active" : ""} data-testid={`tab-${t.route}`}>
            {t.label}
            {t.route === "netz" && s.data && outdated(s.data) ? " •" : ""}
          </a>
        ))}
      </nav>
    </div>
  );
}
